import { QueryClient } from '@tanstack/react-query';
import { isAppError, isNonRetryable } from '@/lib/api/errors';

/**
 * QueryClient with Almanac-tuned defaults (plan §4.4 / §7).
 * - Don't retry client-fault errors (validation/forbidden/404/conflict).
 * - Retry transient server/network errors twice.
 * - Reasonable staleness; per-query overrides tune schemas/settings (long) vs lists (short).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          if (isNonRetryable(error)) return false;
          // Never retry a 401 here — the client interceptor already handled refresh-retry.
          if (isAppError(error) && error.is('unauthorized')) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
        // Attempt the write even when the browser reports itself offline.
        //
        // TanStack's default (`networkMode: 'online'`) PAUSES a mutation while
        // `navigator.onLine` is false: no request is sent, no error is raised,
        // `isPending` stays true — so the console shows a button spinning
        // forever and says nothing. Every write in this app has an onError that
        // explains itself, and PHASE 09's attendance register additionally
        // keeps a local draft and offers a retry; all of that is dead code
        // under a paused mutation. Firing and failing honestly is the behaviour
        // the screens are written for, and `navigator.onLine` is a poor oracle
        // anyway — it is false on a captive portal and true on a dead Wi-Fi.
        networkMode: 'always',
      },
    },
  });
}
